import { z } from "zod";
import { DocumentFormat } from "../../constants";

const requiredId = (label: string) =>
  z.coerce.number({ invalid_type_error: `Επιλέξτε ${label}` }).refine((v) => v > 0, {
    message: `Επιλέξτε ${label}`,
  });

const optionalText = z.string().optional().or(z.literal(""));

export const surgerySchema = z.object({
  surgeryTypeId: requiredId("τύπο χειρουργείου"),
  surgeryDate: z.string().min(1, "Η ημερομηνία είναι υποχρεωτική"),
  statusDescr: z.string().min(1, "Η κατάσταση είναι υποχρεωτική"),
  category: optionalText,
  startTime: optionalText,
  endTime: optionalText,
  preOpDiagnosis: optionalText,
  postOpDiagnosis: optionalText,
  remarks: optionalText,
});

export const documentSchema = z.object({
  documentTypeId: requiredId("τύπο εγγράφου"),
  fileName: z.string().min(1, "Ο τίτλος είναι υποχρεωτικός"),
  format: z.coerce
    .number()
    .refine((v) => v !== DocumentFormat.NONE, { message: "Επιλέξτε format" }),
  file: z
    .custom<File>((v) => v instanceof File, { message: "Επιλέξτε αρχείο" })
    .refine((f) => f.size > 0, { message: "Το αρχείο είναι κενό" }),
  examinationDate: optionalText,
  medicalCenter: optionalText,
  documentRemarks: optionalText,
  patientSurgeryIds: z.array(z.coerce.number()).optional().default([]),
});

export const implantSchema = z.object({
  implantId: requiredId("εμφύτευμα"),
  serialNumber: z.string().min(1, "Το serial number είναι υποχρεωτικό"),
  lotNumber: z.string().min(1, "Το lot number είναι υποχρεωτικό"),
  anatomicLocation: z.string().min(1, "Η ανατομική θέση είναι υποχρεωτική"),
});

export const medicineSchema = z.object({
  medicineId: requiredId("φάρμακο"),
  doseId: requiredId("δόση"),
  datePrescribed: z.string().min(1, "Η ημερομηνία είναι υποχρεωτική"),
  duration: optionalText,
  patMedRemarks: optionalText,
});

export const allergySchema = z.object({
  allergyId: requiredId("αλλεργία"),
  patAllRemarks: optionalText,
});

export const clinicalScoreSchema = z.object({
  assessmentDate: z.string().min(1, "Η ημερομηνία είναι υποχρεωτική"),
  vasPainScore: z.coerce
    .number({ invalid_type_error: "Η βαθμολογία VAS είναι υποχρεωτική" })
    .min(0, "Ελάχιστο 0")
    .max(10, "Μέγιστο 10"),
  mobilityScore: z.string().min(1, "Η βαθμολογία κινητικότητας είναι υποχρεωτική"),
  scoreRemarks: optionalText,
});

export const rehabProtocolSchema = z.object({
  rehabId: requiredId("πρωτόκολλο / κέντρο"),
  status: z.string().min(1, "Η κατάσταση είναι υποχρεωτική"),
  totalSessionsPrescribed: z.coerce
    .number({ invalid_type_error: "Υποχρεωτικό πεδίο" })
    .min(1, "Τουλάχιστον 1 συνεδρία"),
  sessionsCompleted: z.coerce
    .number({ invalid_type_error: "Υποχρεωτικό πεδίο" })
    .min(0, "Δεν μπορεί να είναι αρνητικό"),
});

export type SurgeryFormValues = z.infer<typeof surgerySchema>;
export type DocumentFormValues = z.infer<typeof documentSchema>;
export type ImplantFormValues = z.infer<typeof implantSchema>;
export type MedicineFormValues = z.infer<typeof medicineSchema>;
export type AllergyFormValues = z.infer<typeof allergySchema>;
export type ClinicalScoreFormValues = z.infer<typeof clinicalScoreSchema>;
export type RehabProtocolFormValues = z.infer<typeof rehabProtocolSchema>;
